import spartaEventsUtility from '@sparta.global.utilities/sparta-events-utility/dist/es6.js';
import spartaTransportUtility from '@sparta.global.utilities/sparta-transport-utility/dist/es6.js';
import spartaGlobalFunctionsUtility from '@sparta.global.utilities/sparta-global-functions-utility/dist/es6.js';
import spartaContentManagerUtility from '@sparta.global.utilities/sparta-content-manager-utility/dist/es6.js';

/**
 * @name        Data Asset Loader
 * @file        index.js
 * @description Dynamically loading assets from a utility's data directory
 * @classdesc   Dynamically loading assets from a utility's data directory
 */


// const to keep Global funstions api
const GlobalAPIByFileType = {
  js: spartaGlobalFunctionsUtility.loadScriptByUrl,
  css: spartaGlobalFunctionsUtility.loadCssByUrl
};

/**
 * @method ajaxComplete
 * @description emit the relevant spartaEventsUtility event after loading via AJAX
 * @param {Object} xhr the data returned by the Sparta Transport method
 * @param {String} filename name of the asset we loaded
 * @param {Object} events Event object
 * @returns {void}
 */
async function ajaxComplete(xhr, filename, events) {
  // generate event data Object to pass to spartaEventsUtility subscribers
  const evtData = {
    filename,
    xhr
  };

  // if the XHR succeeded or we just got back a JSON Object
  // NOTE: Transport doesn't preserve the XHR data on success
  if (xhr.status >= 200 && xhr.status < 400 || !xhr.status && !xhr.DONE) {
    // publish the success event
    const content = await spartaContentManagerUtility.processWebContents(xhr);
    spartaEventsUtility.pub(events.success, {
      filename: filename,
      xhr: content
    });
  } else {
    // publish the failure event
    spartaEventsUtility.pub(events.failure, evtData);
  }
}
function prependPathPrefix(dataAssets, pathPrefix) {
  if (dataAssets && !dataAssets.pathPrefixed) {
    dataAssets.pathPrefixed = true;
    for (const assetKey in dataAssets) {
      dataAssets[assetKey] = `${pathPrefix}/${dataAssets[assetKey]}`;
    }
  }
  return dataAssets;
}

/**
 * @class DataAssetLoader
 * @description Class to make it easy to work with dynamically loading assets for
 * a Sparta Component
 */
class DataAssetLoader {
  /**
   * @method constructor
   * @description Creates an instance of DataAssetLoader.
   * @param {string} assetScopedName the name of the Sparta Component in `site/component-name` form
   * @param {string} [assetLocation='data'] the path in the component's directory to load assets from
   * @memberof DataAssetLoader
   */
  constructor(assetScopedName, assetLocation = 'data') {
    // Exit early if we were not given asset scoped name.
    if (!assetScopedName) {
      return;
    }
    this.assetScopedName = assetScopedName;
    this.assetLocation = assetLocation;
    const spaParams = spartaGlobalFunctionsUtility.getSpaParams();

    // Ensure that spaParams are loaded before we use it
    if (spaParams) {
      this.spaParamsPromise = Promise.resolve(spaParams);
      const {
        dataAssets
      } = spaParams;
      this.dataAssets = prependPathPrefix(dataAssets[this.assetScopedName], dataAssets.pathPrefix);
    } else {
      this.spaParamsPromise = new Promise(resolve => {
        spartaEventsUtility.sub(spartaEventsUtility.EVENTS.SPA_PARAMS_LOADED, event => {
          const {
            dataAssets
          } = event.data;
          this.dataAssets = prependPathPrefix(dataAssets[this.assetScopedName], dataAssets.pathPrefix);
          resolve();
        }, true);
      });
    }
  }

  /**
   * @method loadAsset
   * @description Retrieves JSON files associated with a rule from the data folder.
   * @param {String} filename the name of the file to load from our assetLocation
   * @param {String} [type='json'] the type of asset we are loading
   * @memberof DataAssetLoader
   * @returns {Promise|boolean} Promise contains a list of events. False if not filename or config.
   */
  loadAsset(filename, type = 'json') {
    // Return early if we're missing a filename or configuration.
    if (!filename) {
      // Return false so the component knows not to listen for anything.
      return false;
    }
    const success = Math.floor(Math.random() * 100000) + '';
    const failure = Math.floor(Math.random() * 100000) + '';
    const loadEvents = {
      success,
      failure
    };
    this.spaParamsPromise.then(() => {
      switch (type) {
        case 'js':
        case 'css':
          this.loadFile(filename, type, loadEvents);
          break;
        case 'json':
        default:
          this.loadViaAjax(filename, type, loadEvents);
      }
    });
    return loadEvents;
  }

  /**
   * @method loadFile
   * @description loads a file by its name
   * @param {String} filename name of the asset to load
   * @param {Object} evts Event object
   * @memberof DataAssetLoader
   */
  loadFile(filename, type, evts) {
    const assetUrl = this.buildAssetUrl(filename);
    if (assetUrl && GlobalAPIByFileType[type]) {
      GlobalAPIByFileType[type](assetUrl).then(spartaEventsUtility.pub.bind(spartaEventsUtility, evts.success, {
        filename
      })).catch(spartaEventsUtility.pub.bind(spartaEventsUtility, evts.failure, {
        filename
      }));
    } else {
      // Asset not found or file type not supported
      spartaEventsUtility.pub(evts.failure, {
        filename,
        type
      });
    }
  }

  /**
   * @method loadViaAjax
   * @description loads a JSON asset via spartaTransportUtility
   * @param {String} filename name of the asset to load
   * @param {String} type type of the asset to load. E.g. json
   * @param {Object} evts Event object
   * @memberof DataAssetLoader
   */
  loadViaAjax(filename, type, evts) {
    const assetUrl = this.buildAssetUrl(filename, true);
    if (!assetUrl) {
      spartaEventsUtility.pub(evts.failure, {
        filename,
        type
      });
      return false;
    }

    // create the XHR Object for spartaTransportUtility
    const subscription = {
      cache: true,
      dataType: type,
      method: 'GET',
      error(xhr) {
        ajaxComplete(xhr, filename, evts);
      },
      success(data) {
        ajaxComplete(data, filename, evts);
      },
      url: assetUrl
    };
    spartaTransportUtility.subscribe(subscription);
  }

  /**
   * @method buildAssetUrl
   * @description create the Url where the given asset can be found
   * @param {String} filename name of the asset to load
   * @param {Boolean} appendQS append querystring
   * @returns {String} Asset Url
   * @memberof DataAssetLoader
   */
  buildAssetUrl(filename, appendQS) {
    let url = this.dataAssets ? this.dataAssets[`${this.assetLocation}/${filename}`] : null;
    if (url) {
      url = `${spartaGlobalFunctionsUtility.getSpaAssetsUrlPrefix()}/${url}`;
      if (appendQS) {
        url += url.includes('.bankofamerica.com') ? '?ro=boa' : url.includes('.ml.com') ? '?ro=ml' : '';
      }
    }
    return url;
  }
}

export { DataAssetLoader as default };
